import React, {
  ChangeEvent,
  DOMAttributes,
  FocusEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { MapPinIcon } from '@heroicons/react/24/solid';

export interface AddressLookupProps extends DOMAttributes<AddressLookupProps> {
  address: Address;
  defaultValue?: string;
  setAddress: (address: Address, addressString: string) => void;
  setValid?: (valid: boolean) => void;
  name?: string | undefined;
  callback?: (event: any) => void;
  eecBillingInfo?: any;
  billingInfo?: any;
  billingEventSentToGTM?: boolean;
  setBillingEventSentToGTM?: (status: boolean) => void;
  onlyUnitedKingdom?: boolean;
  idPrefix?: string;
  allowedCities?: string[];
  label?: string;
}

export interface Address {
  line1?: string;
  line2?: string;
  city?: string;
  county?: string;
  postCode?: string;
  country?: string;
}

type PostCoderFind = {
  id: string;
  type: string;
  summaryline: string;
  count: number;
};

type PostCoderRetrieve = {
  addressline1: string;
  addressline2: string;
  summaryline: string;
  subbuildingname: string;
  buildingname: string;
  number: string;
  premise: string;
  street: string;
  posttown: string;
  county: string;
  postcode: string;
};

export function simplifyAddress(address: Address): string {
  if (
    !address.line1 &&
    !address.line2 &&
    !address.city &&
    !address.county &&
    !address.postCode &&
    !address.country
  ) {
    return '';
  }

  let addressString = address.line1 + ', ';

  if (address.line2) {
    addressString += address.line2 + ', ';
  }

  addressString += (address.city ?? '') + ', ';
  addressString += (address.county ?? '') + ', ';
  addressString += (address.postCode ?? '') + ', ';
  addressString += address.country ?? '';

  return addressString;
}

export function validateAddress(address: Address) {
  const regex = new RegExp(
    '^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$',
  );

  return (
    address.postCode &&
    regex.test(address.postCode) &&
    address.line1 &&
    address.postCode
  );
}

export function testPostCode(address: Address): JSX.Element | null {
  const regex = new RegExp(
    '^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$',
  );

  // Only show error message if postcode is invalid
  if (address.postCode && !regex.test(address.postCode)) {
    return (
      <div style={{
        color: '#ff5757',
        backgroundColor: '#ffe5e5',
        padding: '12px 16px',
        borderRadius: '8px',
        marginTop: '12px',
        fontSize: '14px',
        fontWeight: '500',
        border: '1px solid #ffcccc'
      }}>
        Please ensure a valid postal code
      </div>
    );
  }
  
  // Return null when valid or empty (no error to show)
  return null;
}

// Function to determine city from UK postcode
export function getCityFromPostcode(postcode: string): string | null {
  if (!postcode) return null;
  
  // Extract the postcode area (first 1-2 letters before the number)
  const postcodeArea = postcode.toUpperCase().match(/^([A-Z]{1,2})/)?.[1];
  
  if (!postcodeArea) return null;
  
  // London postcode areas
  const londonAreas = [
    'E', 'EC', 'N', 'NW', 'SE', 'SW', 'W', 'WC',
    'BR', 'CR', 'DA', 'EN', 'HA', 'IG', 'KT', 'RM', 'SM', 'TW', 'UB'
  ];
  
  // Exeter postcode areas
  const exeterAreas = ['EX'];
  
  // Sheffield postcode areas
  const sheffieldAreas = ['S'];
  
  if (londonAreas.includes(postcodeArea)) {
    return 'London';
  } else if (exeterAreas.includes(postcodeArea)) {
    return 'Exeter';
  } else if (sheffieldAreas.includes(postcodeArea)) {
    return 'Sheffield';
  }
  
  return null;
}

// Function to check if postcode is in allowed cities
export function isPostcodeInAllowedCities(
  postcode: string,
  allowedCities: string[]
): boolean {
  const city = getCityFromPostcode(postcode);
  return city !== null && allowedCities.includes(city);
}

async function loadAddress(
  id: string,
  query: string,
  setAddress: (address: Address, addressString: string) => void,
) {
  const [result]: PostCoderRetrieve[] = await fetch(
    `https://ws.postcoder.com/pcw/autocomplete/retrieve/?id=${id}&query=${query}&country=uk&lines=2&apikey=${process.env.REACT_APP_POSTCODER_API_KEY}`,
  ).then((response) => response.json());

  const address = {
    line1: result.addressline1,
    line2: result.addressline2,
    city: result.posttown,
    county: result.county,
    postCode: result.postcode,
    country: 'United Kingdom',
  };

  setAddress(address, simplifyAddress(address));
}

export default function AddressLookup({
  address,
  setAddress,
  setValid,
  children,
  defaultValue = '',
  callback,
  eecBillingInfo,
  billingInfo,
  billingEventSentToGTM,
  setBillingEventSentToGTM,
  onlyUnitedKingdom,
  idPrefix = '',
  allowedCities,
  label = 'Address lookup',
}: AddressLookupProps) {
  const [query, setQuery] = useState(defaultValue);
  const [suggestions, setSuggestions] = useState<PostCoderFind[]>([]);
  const [active, setActive] = useState(false);
  const [cityValidationError, setCityValidationError] = useState<string | null>(null);
  const selectionRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: Event) {
      const node = event.target as Node;
      if (
        !selectionRef?.current?.contains(node) &&
        !inputRef?.current?.contains(node)
      ) {
        setActive(false);
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectionRef, inputRef]);

  useEffect(() => {
    // Validate city if allowedCities is provided
    if (allowedCities && address.postCode) {
      const isAllowed = isPostcodeInAllowedCities(address.postCode, allowedCities);
      const detectedCity = getCityFromPostcode(address.postCode);
      
      if (!isAllowed && detectedCity) {
        setCityValidationError(
          `Sorry, we currently only service ${allowedCities.join(', ')} for removals. The postcode you entered appears to be in ${detectedCity}.`
        );
      } else if (!isAllowed && !detectedCity) {
        setCityValidationError(
          `Sorry, we currently only service ${allowedCities.join(', ')} for removals.`
        );
      } else {
        setCityValidationError(null);
      }
    } else {
      setCityValidationError(null);
    }

    const isAddressValid = validateAddress(address) && !cityValidationError;

    if (setValid) {
      setValid(!!isAddressValid);
    }

    if (
      isAddressValid &&
      callback &&
      !billingEventSentToGTM &&
      setBillingEventSentToGTM
    ) {
      setBillingEventSentToGTM(true);
      callback(eecBillingInfo);
      callback(billingInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, setValid, allowedCities, cityValidationError]);

  const onChange =
    (key: string) =>
    (value: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>) => {
      const updatedAddress: Address = {
        ...address,
        [key]: value.target.value,
      };

      if (onlyUnitedKingdom) {
        updatedAddress.country = 'United Kingdom';
      }

      if (setValid) {
        setValid(!!validateAddress(updatedAddress));
      }
      setAddress(updatedAddress, simplifyAddress(updatedAddress));
    };

  useEffect(() => {
    (async () => {
      const suggestions: PostCoderFind[] = await fetch(
        `https://ws.postcoder.com/pcw/autocomplete/find?query=${query}&country=uk&singlesummary=true&apikey=${process.env.REACT_APP_POSTCODER_API_KEY}`,
      ).then((response) => response.json());
      setSuggestions(
        suggestions.filter((suggestion) => suggestion.type === 'ADD'),
      );
    })();
  }, [query]);

  return (
    <div className="coll-del-div addressLookup">
      <div className="title-and-input">
        <div className="title">
          <section>{label}:</section>
          <MapPinIcon
            style={{ width: '30px', height: '30px', color: '#604B8D' }}
          />
        </div>
        <input
          className="lookup"
          placeholder="Type address here..."
          ref={inputRef}
          onFocus={() => setActive(true)}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        {active && (
          <div ref={selectionRef} className="description address">
            <ul>
              {suggestions.map((suggestion) => (
                <li
                  key={suggestion.id}
                  onClick={() => {
                    loadAddress(suggestion.id, query, setAddress);
                    setActive(false);
                    setQuery('');
                  }}
                >
                  <strong>{suggestion.summaryline}</strong>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="full-address-info">
          <AddressInput
            address={address}
            onChange={onChange}
            name="line1"
            placeholder="Address line 1"
            idPrefix={idPrefix}
          />
          <AddressInput
            address={address}
            onChange={onChange}
            name="line2"
            placeholder="Address line 2"
            idPrefix={idPrefix}
          />
          <AddressInput
            address={address}
            onChange={onChange}
            name="city"
            placeholder="City"
            idPrefix={idPrefix}
          />
          <AddressInput
            address={address}
            onChange={onChange}
            name="county"
            placeholder="County"
            idPrefix={idPrefix}
          />
          <AddressInput
            address={address}
            onChange={onChange}
            name="postCode"
            placeholder="Post code"
            idPrefix={idPrefix}
          />
          <AddressInput
            address={address}
            onChange={onChange}
            name="country"
            placeholder="Country"
            readOnly={onlyUnitedKingdom}
            defaultValue={onlyUnitedKingdom ? 'United Kingdom' : undefined}
            idPrefix={idPrefix}
          />
        </div>
        {cityValidationError && (
          <div className="city-validation-error" style={{
            color: '#ff5757',
            backgroundColor: '#ffe5e5',
            padding: '12px 16px',
            borderRadius: '8px',
            marginTop: '12px',
            fontSize: '14px',
            fontWeight: '500',
            border: '1px solid #ffcccc'
          }}>
            {cityValidationError}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

interface AddressInputProps {
  onChange: (
    key: string,
  ) => (
    value: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>,
  ) => void;
  placeholder: string;
  name: keyof Address;
  address: Address;
  readOnly?: boolean;
  defaultValue?: string;
  idPrefix?: string;
}

function AddressInput({
  onChange,
  placeholder,
  name,
  address,
  readOnly = false,
  defaultValue,
  idPrefix = '',
}: AddressInputProps) {
  const inputId = idPrefix ? `${idPrefix}-${name}` : name;

  return (
    <div key={name} className="each">
      <input
        id={inputId}
        onChange={onChange(name)}
        onBlur={onChange(name)}
        value={address[name]}
        defaultValue={defaultValue}
        readOnly={readOnly}
      />
      <div className="line" />
      <label className="address-label" htmlFor={inputId}>
        {placeholder}
      </label>
    </div>
  );
}
